import type { i18n as I18nInstance, Interpolator } from 'i18next';
import type { TextDirection } from './text-direction';

const FIRST_STRONG_ISOLATE = '\u2068';
const LEFT_TO_RIGHT_ISOLATE = '\u2066';
const POP_DIRECTIONAL_ISOLATE = '\u2069';

export const isolateBidi = (value: string): string =>
  value.length === 0 ||
  (value.startsWith(FIRST_STRONG_ISOLATE) &&
    value.endsWith(POP_DIRECTIONAL_ISOLATE))
    ? value
    : `${FIRST_STRONG_ISOLATE}${value}${POP_DIRECTIONAL_ISOLATE}`;

export const isolateLtr = (value: string): string =>
  value.length === 0 ||
  (value.startsWith(LEFT_TO_RIGHT_ISOLATE) &&
    value.endsWith(POP_DIRECTIONAL_ISOLATE))
    ? value
    : `${LEFT_TO_RIGHT_ISOLATE}${value}${POP_DIRECTIONAL_ISOLATE}`;

export const formatDialCode = (
  dialCode: string,
  direction: TextDirection
): string => {
  const value = `(+${dialCode})`;

  return direction === 'rtl' ? isolateLtr(value) : value;
};

/**
 * Isolates every interpolated value so neutral characters next to it (a
 * trailing period, the `*` of a masked email, parentheses) resolve against
 * the value's own direction instead of the surrounding paragraph's.
 * The RTL gate keeps LTR output byte-identical and free of invisible controls.
 *
 * Patches the interpolator after init rather than passing
 * `interpolation.format` into `init`: i18next only installs its built-in
 * formatter when no custom format function is supplied, so passing one would
 * silently drop the named formatters the i18n-toolkit registers.
 */
export const applyBidiIsolationToInterpolations = (
  instance: I18nInstance,
  getDirection: (language: string) => TextDirection
): void => {
  type InterpolatorFormat = (
    value: unknown,
    format?: string,
    lng?: string,
    options?: Record<string, unknown>
  ) => unknown;
  interface MutableInterpolator extends Interpolator {
    alwaysFormat: boolean;
    format: InterpolatorFormat;
  }

  const interpolator = instance.services.interpolator;
  if (!interpolator) {
    return;
  }

  const mutableInterpolator = interpolator as MutableInterpolator;
  const baseFormat = mutableInterpolator.format.bind(mutableInterpolator);

  const interpolationOptions = instance.options.interpolation;
  if (interpolationOptions) {
    interpolationOptions.alwaysFormat = true;
  }
  mutableInterpolator.alwaysFormat = true;
  mutableInterpolator.format = (value, format, lng, options) => {
    const result = format ? baseFormat(value, format, lng, options) : value;
    if (
      getDirection(lng ?? instance.language) !== 'rtl' ||
      result === null ||
      result === undefined ||
      String(result).length === 0
    ) {
      return result;
    }

    return isolateBidi(String(result));
  };
};
